import { useDebounceFn } from '@vueuse/core'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import { useGlobalFilters } from '~/compositions/globalFilters'
import { isRequestAbort } from '~/requestAbort'
import { ExportsApi } from '~/services/apiClient'

// A burst of booking updates (an upload, a terminal sync) shouldn't turn into a
// request each: the last one in the burst is the one that matters.
const RELOAD_DEBOUNCE_MS = 1_000
const RELOAD_MAX_WAIT_MS = 5_000

/**
 * The schedules this org has an active booking on ("My Active Vessels"), by
 * `GETVesselSchedule.id`. Kept apart from the schedule rows on purpose:
 * `/vessels/etas/` is pushed to every org over a shared websocket, so a per-org
 * flag can't live on those rows.
 */
export const useActiveVesselSchedulesStore = defineStore(
  'active-vessel-schedules',
  () => {
    const scheduleIds = shallowRef<ReadonlySet<number>>(new Set())
    const hasActiveBookings = ref(false)
    // Only true after a successful load, so a failed first request can't read
    // as "this org has no bookings".
    const loaded = ref(false)
    const loading = ref(false)
    let abortController: AbortController | undefined

    // Newest call wins: a response that predates a booking change must not land
    // after the one that follows it.
    async function load(): Promise<void> {
      abortController?.abort()
      const controller = new AbortController()
      abortController = controller
      loading.value = true
      try {
        const response =
          await new ExportsApi().getActiveVesselSchedulesExportsActiveVesselSchedulesGet(
            useGlobalFilters().portComplexes.value,
            { signal: controller.signal }
          )
        if (controller.signal.aborted) return
        scheduleIds.value = new Set(response.data.vessel_schedule_ids)
        hasActiveBookings.value = response.data.has_active_bookings
        loaded.value = true
      } catch (error) {
        if (!isRequestAbort(error)) throw error
      } finally {
        if (abortController === controller) {
          abortController = undefined
          loading.value = false
        }
      }
    }

    const reloadSoon = useDebounceFn(
      () => load().catch(() => {}), // the API client already reports failures
      RELOAD_DEBOUNCE_MS,
      { maxWait: RELOAD_MAX_WAIT_MS }
    )

    /**
     * For a booking change pushed over the websocket (`EXPORT_UPDATED`). Nothing
     * happens until the Schedule board has asked once, so users who never open
     * it pay no requests.
     */
    function reloadIfLoaded() {
      if (loaded.value || loading.value) void reloadSoon()
    }

    return {
      scheduleIds,
      hasActiveBookings,
      loaded,
      loading,
      load,
      reloadIfLoaded,
    }
  }
)

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useActiveVesselSchedulesStore, import.meta.hot)
  )
}
